import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../context/ModalDialogContext';

const DEFAULT_CATEGORIES = [
  'Fish',
  'Mammals',
  'Birds',
  'Reptiles',
  'Amphibians',
  'Invertebrates',
  'Other'
];

const CATEGORY_ICONS = {
  Fish: '🐟',
  Mammals: '🦌',
  Birds: '🦅',
  Reptiles: '🦎',
  Amphibians: '🐸',
  Invertebrates: '🦀',
  Other: '🐾'
};

const CATEGORY_COLORS = {
  Fish: { bg: 'rgba(56, 189, 248, 0.15)', text: '#38bdf8', border: 'rgba(56, 189, 248, 0.35)' },
  Mammals: { bg: 'rgba(234, 179, 8, 0.15)', text: '#eab308', border: 'rgba(234, 179, 8, 0.35)' },
  Birds: { bg: 'rgba(168, 85, 247, 0.15)', text: '#c084fc', border: 'rgba(168, 85, 247, 0.35)' },
  Reptiles: { bg: 'rgba(34, 197, 94, 0.15)', text: '#4ade80', border: 'rgba(34, 197, 94, 0.35)' },
  Amphibians: { bg: 'rgba(20, 184, 166, 0.15)', text: '#2dd4bf', border: 'rgba(20, 184, 166, 0.35)' },
  Invertebrates: { bg: 'rgba(244, 63, 94, 0.15)', text: '#fb7185', border: 'rgba(244, 63, 94, 0.35)' },
  Other: { bg: 'rgba(148, 163, 184, 0.15)', text: '#94a3b8', border: 'rgba(148, 163, 184, 0.35)' }
};

const EMPTY_FORM = {
  common_name: '',
  scientific_name: '',
  category: 'Fish',
  subcategory: '',
  kingdom: 'Animalia',
  phylum: '',
  class_name: '',
  order: '',
  family: '',
  genus: '',
  species: '',
  teeth: '',
  body: '',
  jaws: '',
  size_weight: '',
  distinctive_features: '',
  predation: '',
  diet: '',
  social_structure: '',
  distribution: '',
  conservation_status: 'Least Concern (IUCN)',
  summary: '',
  image_url: '',
  tenant_id: ''
};

export default function SpeciesLibrary({ user }) {
  const modal = useModal();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const isGlobalRole = user?.role === 'GLOBAL_ADMIN' || user?.role === 'GLOBAL_POWER' || user?.role === 'GLOBAL_STANDARD';
  const isTenantAdmin = user?.role === 'TENANT_ADMIN';

  // Data states
  const [speciesList, setSpeciesList] = useState([]);
  const [allSpecies, setAllSpecies] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [allCategories, setAllCategories] = useState(DEFAULT_CATEGORIES);
  const [tenants, setTenants] = useState([]);
  const [tenantFilters, setTenantFilters] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters & Search
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [simulatedTenantId, setSimulatedTenantId] = useState('all');

  // Modals
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [modalError, setModalError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState(null);

  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [selectedVisibilityTenant, setSelectedVisibilityTenant] = useState('');
  const [visibilityAllowedCats, setVisibilityAllowedCats] = useState([]);

  // AI Lookup State
  const [aiLookupQuery, setAiLookupQuery] = useState('');
  const [aiLookupLoading, setAiLookupLoading] = useState(false);
  const [aiLookupSource, setAiLookupSource] = useState(null);

  // Toast alerts
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    if (modal?.notify) modal.notify(msg, 'success');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const showError = (msg) => {
    setErrorMsg(msg);
    if (modal?.notify) modal.notify(msg, 'error');
    setTimeout(() => setErrorMsg(''), 5000);
  };

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (successMsg) modal.notify(successMsg, 'success');
  }, [successMsg, modal]);

  useEffect(() => {
    if (errorMsg) modal.notify(errorMsg, 'error');
  }, [errorMsg, modal]);

  // 1. Fetch Species List
  const fetchSpecies = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/v1/species?`;
      const params = new URLSearchParams();
      if (isGlobalRole && simulatedTenantId !== 'all') {
        params.append('tenant_id', simulatedTenantId);
      }

      const res = await fetch(`${url}${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setAllSpecies(list);
        setSpeciesList(list);
      } else {
        const err = await res.json().catch(() => ({}));
        showError(err.detail || 'Failed to fetch species library.');
      }
    } catch (err) {
      console.error('Error fetching species:', err);
      showError('Network error connecting to backend species service.');
    } finally {
      setLoading(false);
    }
  };

  // Memoized client-side filtering for instantaneous category & search updates
  const displayedSpecies = useMemo(() => {
    const rawSource = Array.isArray(allSpecies) && allSpecies.length > 0 ? allSpecies : speciesList;
    const source = Array.isArray(rawSource) ? rawSource : [];
    return source.filter((sp) => {
      const matchCat = activeCategory === 'All' || (sp.category || '').toLowerCase() === activeCategory.toLowerCase();
      if (!matchCat) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.trim().toLowerCase();
      const common = (sp.common_name || '').toLowerCase();
      const scientific = (sp.scientific_name || '').toLowerCase();
      const cat = (sp.category || '').toLowerCase();
      const summary = (sp.summary || '').toLowerCase();
      const habitat = (sp.behavior_and_diet?.habitat || '').toLowerCase();
      const status = (sp.behavior_and_diet?.conservation_status || '').toLowerCase();

      return (
        common.includes(q) ||
        scientific.includes(q) ||
        cat.includes(q) ||
        summary.includes(q) ||
        habitat.includes(q) ||
        status.includes(q)
      );
    });
  }, [allSpecies, speciesList, activeCategory, searchQuery]);

  // 2. Fetch categories for current scope (tenant-specific or global)
  const fetchCategories = async (tenantId) => {
    try {
      let url = `${API_BASE}/api/v1/species/categories`;
      const tId = tenantId !== undefined ? tenantId : simulatedTenantId;
      if (isGlobalRole && tId && tId !== 'all') {
        url += `?tenant_id=${tId}`;
      }
      const resCat = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resCat.ok) {
        const catData = await resCat.json();
        const nextCategories = Array.isArray(catData) && catData.length > 0 ? catData : DEFAULT_CATEGORIES;
        setCategories(nextCategories);
        setActiveCategory((prevActive) => {
          if (prevActive === 'All') return 'All';
          const match = nextCategories.some((c) => c.toLowerCase() === prevActive.toLowerCase());
          return match ? prevActive : 'All';
        });
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // 3. Fetch metadata (tenants, tenant filters, all categories)
  const fetchMetadata = async () => {
    try {
      // Fetch all system categories for global admin category visibility modal
      const resAllCat = await fetch(`${API_BASE}/api/v1/species/categories?all=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resAllCat.ok) {
        const allCatData = await resAllCat.json();
        setAllCategories(Array.isArray(allCatData) && allCatData.length > 0 ? allCatData : DEFAULT_CATEGORIES);
      }

      await fetchCategories(simulatedTenantId);

      if (isGlobalRole) {
        const resTenants = await fetch(`${API_BASE}/api/v1/users/tenants`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resTenants.ok) {
          const tData = await resTenants.json();
          setTenants(tData);
          if (tData.length > 0 && !selectedVisibilityTenant) {
            setSelectedVisibilityTenant(String(tData[0].id));
          }
        }

        const resFilters = await fetch(`${API_BASE}/api/v1/species/tenant-filters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resFilters.ok) {
          const fData = await resFilters.json();
          setTenantFilters(fData);
        }
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  useEffect(() => {
    fetchMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSpecies();
    fetchCategories(simulatedTenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulatedTenantId]);

  // Categories available in Add/Edit modal based on assigned tenant
  const modalCategories = useMemo(() => {
    if (!isGlobalRole) return categories;
    if (formData.tenant_id) {
      const match = tenantFilters.find((f) => String(f.tenant_id) === String(formData.tenant_id));
      if (match && match.allowed_categories && match.allowed_categories.length > 0) {
        return match.allowed_categories;
      }
    } else if (simulatedTenantId !== 'all') {
      return categories;
    }
    return allCategories.length > 0 ? allCategories : DEFAULT_CATEGORIES;
  }, [isGlobalRole, categories, formData.tenant_id, tenantFilters, simulatedTenantId, allCategories]);

  // Keep modal category in sync with modalCategories
  useEffect(() => {
    if (showAddEditModal && modalCategories && modalCategories.length > 0) {
      if (!modalCategories.includes(formData.category)) {
        setFormData((prev) => ({ ...prev, category: modalCategories[0] }));
      }
    }
  }, [showAddEditModal, modalCategories, formData.category]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setEditingSpecies(null);
    const initialCategory = categories.length > 0 ? categories[0] : 'Fish';
    setFormData({
      ...EMPTY_FORM,
      category: initialCategory,
      tenant_id: isGlobalRole && simulatedTenantId !== 'all' ? simulatedTenantId : ''
    });
    setModalError('');
    setAiLookupQuery('');
    setAiLookupSource(null);
    setShowAddEditModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (sp) => {
    setEditingSpecies(sp);
    setModalError('');
    const classification = sp.scientific_classification || {};
    const physical = sp.physical_characteristics || {};
    const behavior = sp.behavior_and_diet || {};

    setFormData({
      common_name: sp.common_name || '',
      scientific_name: sp.scientific_name || '',
      category: sp.category || 'Fish',
      subcategory: sp.subcategory || '',
      kingdom: classification.kingdom || 'Animalia',
      phylum: classification.phylum || '',
      class_name: classification.class || '',
      order: classification.order || '',
      family: classification.family || '',
      genus: classification.genus || '',
      species: classification.species || '',
      teeth: physical.teeth || '',
      body: physical.body || '',
      jaws: physical.jaws || '',
      size_weight: physical.size_weight || '',
      distinctive_features: physical.distinctive_features || '',
      predation: behavior.predation || '',
      diet: behavior.diet || '',
      social_structure: behavior.social_structure || '',
      distribution: sp.distribution || '',
      conservation_status: sp.conservation_status || 'Least Concern (IUCN)',
      summary: sp.summary || '',
      image_url: sp.image_url || '',
      tenant_id: sp.tenant_id ? String(sp.tenant_id) : ''
    });
    setAiLookupQuery(sp.common_name || '');
    setAiLookupSource(null);
    setShowAddEditModal(true);
  };

  // AI & Web Lookup
  const handleAiLookup = async () => {
    const q = aiLookupQuery.trim() || formData.common_name.trim();
    if (!q) {
      showError('Please enter a species common or scientific name to look up.');
      return;
    }

    setAiLookupLoading(true);
    setAiLookupSource(null);
    try {
      const lookupTenantId = formData.tenant_id
        ? parseInt(formData.tenant_id, 10)
        : (simulatedTenantId !== 'all' ? parseInt(simulatedTenantId, 10) : (user?.tenant_id || null));

      const res = await fetch(`${API_BASE}/api/v1/species/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: q,
          tenant_id: lookupTenantId
        })
      });

      if (!res.ok) {
        const err = await res.json();
        showError(err.detail || 'Lookup request failed.');
        return;
      }

      const data = await res.json();
      const sc = data.scientific_classification || {};
      const pc = data.physical_characteristics || {};
      const bd = data.behavior_and_diet || {};

      setFormData((prev) => {
        let catToUse = data.category || prev.category || 'Fish';
        if (modalCategories && modalCategories.length > 0) {
          const allowedMatch = modalCategories.find((c) => c.toLowerCase() === (data.category || '').toLowerCase());
          catToUse = allowedMatch || modalCategories[0];
        }

        return {
          ...prev,
          common_name: data.common_name || prev.common_name || q,
          scientific_name: data.scientific_name || prev.scientific_name || '',
          category: catToUse,
        subcategory: data.subcategory || prev.subcategory || '',
        kingdom: sc.kingdom || prev.kingdom || 'Animalia',
        phylum: sc.phylum || prev.phylum || '',
        class_name: sc.class || prev.class_name || '',
        order: sc.order || prev.order || '',
        family: sc.family || prev.family || '',
        genus: sc.genus || prev.genus || '',
        species: sc.species || prev.species || '',
        teeth: pc.teeth || prev.teeth || '',
        body: pc.body || prev.body || '',
        jaws: pc.jaws || prev.jaws || '',
        size_weight: pc.size_weight || prev.size_weight || '',
        distinctive_features: pc.distinctive_features || prev.distinctive_features || '',
        predation: bd.predation || prev.predation || '',
        diet: bd.diet || prev.diet || '',
        social_structure: bd.social_structure || prev.social_structure || '',
        distribution: data.distribution || prev.distribution || '',
        conservation_status: data.conservation_status || prev.conservation_status || 'Least Concern (IUCN)',
        summary: data.summary || prev.summary || '',
        image_url: data.image_url || prev.image_url || ''
      };
    });

      setAiLookupSource(data.lookup_source);
      showSuccess(`✓ Retrieved species taxonomy & characteristics (${data.lookup_source === 'gemini_2_5_flash' ? 'Gemini AI' : 'Wikipedia'})! Review and edit below.`);
    } catch (err) {
      console.error('Error during AI species lookup:', err);
      showError('Failed to connect to species lookup service.');
    } finally {
      setAiLookupLoading(false);
    }
  };

  // Save Species (Create or Update)
  const handleSaveSpecies = async (e) => {
    e.preventDefault();
    if (!formData.common_name.trim()) {
      setModalError('Species Common Name is required.');
      showError('Species Common Name is required.');
      return;
    }

    setModalError('');
    setIsSaving(true);

    const payload = {
      common_name: formData.common_name.trim(),
      scientific_name: formData.scientific_name.trim() || null,
      category: formData.category.trim() || 'Fish',
      subcategory: formData.subcategory.trim() || null,
      scientific_classification: {
        kingdom: formData.kingdom.trim() || 'Animalia',
        phylum: formData.phylum.trim() || undefined,
        class: formData.class_name.trim() || undefined,
        order: formData.order.trim() || undefined,
        family: formData.family.trim() || undefined,
        genus: formData.genus.trim() || undefined,
        species: formData.species.trim() || undefined
      },
      physical_characteristics: {
        teeth: formData.teeth.trim() || undefined,
        body: formData.body.trim() || undefined,
        jaws: formData.jaws.trim() || undefined,
        size_weight: formData.size_weight.trim() || undefined,
        distinctive_features: formData.distinctive_features.trim() || undefined
      },
      behavior_and_diet: {
        predation: formData.predation.trim() || undefined,
        diet: formData.diet.trim() || undefined,
        social_structure: formData.social_structure.trim() || undefined
      },
      distribution: formData.distribution.trim() || null,
      conservation_status: formData.conservation_status.trim() || null,
      summary: formData.summary.trim() || null,
      image_url: formData.image_url.trim() || null,
      tenant_id: formData.tenant_id ? parseInt(formData.tenant_id, 10) : null
    };

    try {
      const isEdit = Boolean(editingSpecies);
      const url = isEdit ? `${API_BASE}/api/v1/species/${editingSpecies.id}` : `${API_BASE}/api/v1/species`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        showSuccess(`✓ Species "${payload.common_name}" ${isEdit ? 'updated' : 'added'} successfully!`);
        setShowAddEditModal(false);
        fetchSpecies();
      } else {
        let errorDetail = 'Failed to save species record.';
        try {
          const err = await res.json();
          if (err && err.detail) errorDetail = err.detail;
        } catch (_) {}
        setModalError(errorDetail);
        showError(errorDetail);
      }
    } catch (err) {
      console.error('Error saving species:', err);
      const msg = 'Network error saving species: ' + (err.message || 'connection failed');
      setModalError(msg);
      showError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Species
  const handleDeleteSpecies = async (sp) => {
    if (!isGlobalRole && !sp.tenant_id) {
      showError('Global Library species can only be deleted by Global Administrators.');
      return;
    }

    const confirmed = await modal.confirm(`Are you sure you want to delete "${sp.common_name}" from the species library?`, {
      title: 'Delete Species',
      confirmText: 'Delete Species',
      danger: true
    });
    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/species/${sp.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        showSuccess(`✓ Species "${sp.common_name}" deleted.`);
        fetchSpecies();
        if (selectedSpecies?.id === sp.id) {
          setShowDetailsModal(false);
        }
      } else {
        let errorDetail = 'Failed to delete species.';
        try {
          const err = await res.json();
          if (err && err.detail) errorDetail = err.detail;
        } catch (_) {}
        showError(errorDetail);
      }
    } catch (err) {
      console.error('Error deleting species:', err);
      showError('Network error deleting species.');
    }
  };

  // Open Visibility Settings Modal
  const handleOpenVisibility = () => {
    if (tenants.length > 0) {
      const initialId = selectedVisibilityTenant || String(tenants[0].id);
      setSelectedVisibilityTenant(initialId);
      const match = tenantFilters.find((f) => String(f.tenant_id) === String(initialId));
      setVisibilityAllowedCats(match ? match.allowed_categories || [] : []);
    }
    setShowVisibilityModal(true);
  };

  const handleSelectVisibilityTenant = (tId) => {
    setSelectedVisibilityTenant(tId);
    const match = tenantFilters.find((f) => String(f.tenant_id) === String(tId));
    setVisibilityAllowedCats(match ? match.allowed_categories || [] : []);
  };

  const handleToggleVisibilityCategory = (cat) => {
    setVisibilityAllowedCats((prev) => {
      if (prev.includes(cat)) {
        return prev.filter((c) => c !== cat);
      } else {
        return [...prev, cat];
      }
    });
  };

  const handleSaveTenantVisibility = async () => {
    if (!selectedVisibilityTenant) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/species/tenant-filters/${selectedVisibilityTenant}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ allowed_categories: visibilityAllowedCats })
      });
      if (res.ok) {
        showSuccess('✓ Tenant species visibility filter saved!');
        setShowVisibilityModal(false);
        await fetchMetadata();
        await fetchCategories(simulatedTenantId);
        await fetchSpecies();
      } else {
        const err = await res.json();
        showError(err.detail || 'Failed to save tenant visibility filter.');
      }
    } catch (err) {
      console.error('Error saving tenant visibility:', err);
      showError('Network error saving visibility settings.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', position: 'relative' }}>
      {/* Alert Banners */}
      {successMsg && (
        <div style={{
          backgroundColor: 'rgba(46, 160, 67, 0.15)',
          border: '1px solid rgba(46, 160, 67, 0.4)',
          borderRadius: '8px',
          color: '#3fb950',
          padding: '12px 18px',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{
          backgroundColor: 'rgba(248, 81, 73, 0.15)',
          border: '1px solid rgba(248, 81, 73, 0.4)',
          borderRadius: '8px',
          color: '#ff7b72',
          padding: '12px 18px',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          {errorMsg}
        </div>
      )}

      {/* Top Header & Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-main, #ffffff)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🐟</span> Species Library & Biological Registry
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
            Capture, classify, and filter wildlife species profiles with AI-assisted scientific taxonomy lookup.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isGlobalRole && (
            <button
              onClick={handleOpenVisibility}
              style={{
                backgroundColor: '#21262d',
                color: '#c9d1d9',
                border: '1px solid #30363d',
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>⚙️</span> Tenant Category Visibility
            </button>
          )}

          <button
            onClick={handleOpenAdd}
            style={{
              backgroundColor: 'var(--primary-color, #38bdf8)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(56, 189, 248, 0.3)'
            }}
          >
            <span>➕</span> Add Species
          </button>
        </div>
      </div>

      {/* Toolbar: Search, Category Pills & Tenant Simulation Dropdown */}
      <div style={{
        background: 'var(--card-bg, #161b22)',
        border: '1px solid var(--border-color, #30363d)',
        borderRadius: '8px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          {/* Search Input */}
          <div style={{ flex: '1', minWidth: '240px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: '10px', top: '9px', fontSize: '13px', opacity: 0.6 }}>🔍</span>
            <input
              type="text"
              placeholder="Search by species common name, scientific name, family..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                backgroundColor: 'var(--input-bg, #0d1117)',
                color: 'var(--text-main, #ffffff)',
                border: '1px solid var(--border-color, #30363d)',
                borderRadius: '6px',
                padding: '7px 12px 7px 32px',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Global User Tenant View Simulator Dropdown */}
          {isGlobalRole && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                Simulate Tenant View:
              </span>
              <select
                value={simulatedTenantId}
                onChange={(e) => setSimulatedTenantId(e.target.value)}
                style={{
                  backgroundColor: 'var(--input-bg, #0d1117)',
                  color: 'var(--text-main, #ffffff)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '6px',
                  padding: '7px 10px',
                  fontSize: '12px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="all">🌐 All Tenants (Global View)</option>
                {tenants.map((t) => {
                  const filter = tenantFilters.find((f) => f.tenant_id === t.id);
                  const allowedStr = filter && filter.allowed_categories && filter.allowed_categories.length > 0
                    ? ` (${filter.allowed_categories.join(', ')})`
                    : ' (All Categories)';
                  return (
                    <option key={t.id} value={t.id}>
                      🏢 {t.brand_name}{allowedStr}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>

        {/* Category Filter Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
          {(() => {
            const catalog = allSpecies.length > 0 ? allSpecies : speciesList;
            return (
              <>
                <button
                  onClick={() => setActiveCategory('All')}
                  style={{
                    padding: '5px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '16px',
                    border: activeCategory === 'All' ? '1px solid var(--primary-color, #38bdf8)' : '1px solid #30363d',
                    backgroundColor: activeCategory === 'All' ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                    color: activeCategory === 'All' ? '#38bdf8' : 'var(--text-mute, #8b949e)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  All Categories ({catalog.length})
                </button>
                {categories.map((cat) => {
                  const count = catalog.filter((s) => (s.category || '').toLowerCase() === cat.toLowerCase()).length;
                  const isSelected = activeCategory.toLowerCase() === cat.toLowerCase();
                  const icon = CATEGORY_ICONS[cat] || '🐾';
                  const colors = CATEGORY_COLORS[cat] || CATEGORY_COLORS.Other;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(isSelected ? 'All' : cat)}
                      style={{
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '16px',
                        border: isSelected ? `1px solid ${colors.text}` : '1px solid #30363d',
                        backgroundColor: isSelected ? colors.bg : 'transparent',
                        color: isSelected ? colors.text : 'var(--text-mute, #8b949e)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      <span>{icon}</span> {cat} {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
                    </button>
                  );
                })}
              </>
            );
          })()}
        </div>
      </div>

      {/* Species Cards Grid */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-mute, #8b949e)', fontSize: '13px' }}>
          Loading species profiles...
        </div>
      ) : displayedSpecies.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          background: 'var(--card-bg, #161b22)',
          border: '1px dashed var(--border-color, #30363d)',
          borderRadius: '10px'
        }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>🔍</div>
          <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', color: 'var(--text-main, #ffffff)' }}>No Species Found</h4>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute, #8b949e)', maxWidth: '420px', marginInline: 'auto' }}>
            No species match the selected filters or search query. Click "+ Add Species" to create a new profile with AI lookup.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '16px'
        }}>
          {displayedSpecies.map((sp) => {
            const catColors = CATEGORY_COLORS[sp.category] || CATEGORY_COLORS.Other;
            const icon = CATEGORY_ICONS[sp.category] || '🐾';
            const classification = sp.scientific_classification || {};
            const physical = sp.physical_characteristics || {};
            const behavior = sp.behavior_and_diet || {};

            return (
              <div
                key={sp.id}
                style={{
                  background: 'var(--card-bg, #161b22)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                }}
              >
                {/* Card Image Banner */}
                <div style={{
                  height: '140px',
                  backgroundColor: '#0d1117',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {sp.image_url ? (
                    <img
                      src={sp.image_url}
                      alt={sp.common_name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ fontSize: '48px', opacity: 0.35 }}>{icon}</div>
                  )}

                  {/* Category Badge overlay */}
                  <span style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    backgroundColor: catColors.bg,
                    color: catColors.text,
                    border: `1px solid ${catColors.border}`,
                    borderRadius: '12px',
                    padding: '3px 8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <span>{icon}</span> {sp.category}
                  </span>

                  {/* Tenant Attribution Badge if applicable */}
                  {sp.tenant_name && sp.tenant_name !== 'Global Library' && (
                    <span style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '10px',
                      backgroundColor: 'rgba(0,0,0,0.75)',
                      color: '#cbd5e1',
                      border: '1px solid #334155',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      🏢 {sp.tenant_name}
                    </span>
                  )}
                </div>

                {/* Card Body */}
                <div style={{ padding: '14px', flex: '1', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>
                      {sp.common_name}
                    </h4>
                    {sp.scientific_name && (
                      <div style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--text-mute, #8b949e)', marginTop: '2px' }}>
                        {sp.scientific_name}
                      </div>
                    )}
                  </div>

                  {/* Quick Classification Chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '11px', color: '#94a3b8' }}>
                    {classification.order && (
                      <span style={{ backgroundColor: '#21262d', padding: '2px 6px', borderRadius: '4px' }}>
                        Order: {classification.order}
                      </span>
                    )}
                    {classification.family && (
                      <span style={{ backgroundColor: '#21262d', padding: '2px 6px', borderRadius: '4px' }}>
                        Family: {classification.family.split('(')[0].trim()}
                      </span>
                    )}
                  </div>

                  {/* Characteristic Snippet */}
                  {(physical.teeth || physical.body || behavior.predation || sp.summary) && (
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '12px',
                      color: 'var(--text-mute, #8b949e)',
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {physical.teeth ? `Teeth: ${physical.teeth}` : sp.summary || physical.body || behavior.predation}
                    </p>
                  )}
                </div>

                {/* Card Footer Actions */}
                <div style={{
                  padding: '10px 14px',
                  borderTop: '1px solid var(--border-color, #30363d)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)'
                }}>
                  <button
                    onClick={() => {
                      setSelectedSpecies(sp);
                      setShowDetailsModal(true);
                    }}
                    style={{
                      padding: '5px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: 'transparent',
                      color: 'var(--primary-color, #38bdf8)',
                      border: '1px solid var(--border-color, #30363d)',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    📖 View Dossier
                  </button>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleOpenEdit(sp)}
                      title="Edit Species"
                      style={{
                        padding: '5px 8px',
                        fontSize: '12px',
                        backgroundColor: '#21262d',
                        color: '#c9d1d9',
                        border: '1px solid #30363d',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    {(!sp.tenant_id ? isGlobalRole : (isGlobalRole || isTenantAdmin)) && (
                      <button
                        onClick={() => handleDeleteSpecies(sp)}
                        title="Delete Species"
                        style={{
                          padding: '5px 8px',
                          fontSize: '12px',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          color: '#f87171',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. VIEW DOSSIER MODAL (Matches user's Tiger fish format)                   */}
      {/* ========================================================================= */}
      {showDetailsModal && selectedSpecies && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg, #161b22)',
            border: '1px solid var(--border-color, #30363d)',
            borderRadius: '12px',
            width: '680px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 16px 40px rgba(0,0,0,0.7)',
            color: 'var(--text-main, #ffffff)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>{CATEGORY_ICONS[selectedSpecies.category] || '🐾'}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>
                    {selectedSpecies.common_name}
                  </h3>
                  {selectedSpecies.scientific_name && (
                    <span style={{ fontSize: '13px', fontStyle: 'italic', color: 'var(--text-mute, #94a3b8)' }}>
                      {selectedSpecies.scientific_name}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#8b949e',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div style={{ padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Optional Photo Banner */}
              {selectedSpecies.image_url && (
                <div style={{ width: '100%', height: '220px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#0d1117' }}>
                  <img
                    src={selectedSpecies.image_url}
                    alt={selectedSpecies.common_name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}

              {/* Summary */}
              {selectedSpecies.summary && (
                <div style={{
                  padding: '12px 14px',
                  backgroundColor: 'rgba(56, 189, 248, 0.08)',
                  border: '1px solid rgba(56, 189, 248, 0.25)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#e2e8f0',
                  lineHeight: '1.5'
                }}>
                  {selectedSpecies.summary}
                </div>
              )}

              {/* 1. Scientific Classification */}
              <div style={{ backgroundColor: '#0d1117', border: '1px solid #21262d', borderRadius: '8px', padding: '14px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Scientific Classification
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', fontSize: '12px' }}>
                  <div><strong style={{ color: '#94a3b8' }}>Kingdom:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification?.kingdom || 'Animalia'}</span></div>
                  {selectedSpecies.scientific_classification?.phylum && (
                    <div><strong style={{ color: '#94a3b8' }}>Phylum:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification.phylum}</span></div>
                  )}
                  {selectedSpecies.scientific_classification?.class && (
                    <div><strong style={{ color: '#94a3b8' }}>Class:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification.class}</span></div>
                  )}
                  {selectedSpecies.scientific_classification?.order && (
                    <div><strong style={{ color: '#94a3b8' }}>Order:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification.order}</span></div>
                  )}
                  {selectedSpecies.scientific_classification?.family && (
                    <div><strong style={{ color: '#94a3b8' }}>Family:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification.family}</span></div>
                  )}
                  {selectedSpecies.scientific_classification?.genus && (
                    <div><strong style={{ color: '#94a3b8' }}>Genus:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification.genus}</span></div>
                  )}
                  {selectedSpecies.scientific_classification?.species && (
                    <div><strong style={{ color: '#94a3b8' }}>Species:</strong> <span style={{ color: '#f1f5f9' }}>{selectedSpecies.scientific_classification.species}</span></div>
                  )}
                </div>
              </div>

              {/* 2. Physical Characteristics */}
              <div style={{ backgroundColor: '#0d1117', border: '1px solid #21262d', borderRadius: '8px', padding: '14px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#eab308', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Physical Characteristics
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', lineHeight: '1.45' }}>
                  {selectedSpecies.physical_characteristics?.teeth && (
                    <div><strong style={{ color: '#cbd5e1' }}>Teeth:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.physical_characteristics.teeth}</span></div>
                  )}
                  {selectedSpecies.physical_characteristics?.body && (
                    <div><strong style={{ color: '#cbd5e1' }}>Body:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.physical_characteristics.body}</span></div>
                  )}
                  {selectedSpecies.physical_characteristics?.jaws && (
                    <div><strong style={{ color: '#cbd5e1' }}>Jaws:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.physical_characteristics.jaws}</span></div>
                  )}
                  {selectedSpecies.physical_characteristics?.size_weight && (
                    <div><strong style={{ color: '#cbd5e1' }}>Size & Weight:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.physical_characteristics.size_weight}</span></div>
                  )}
                  {selectedSpecies.physical_characteristics?.distinctive_features && (
                    <div><strong style={{ color: '#cbd5e1' }}>Distinctive Features:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.physical_characteristics.distinctive_features}</span></div>
                  )}
                </div>
              </div>

              {/* 3. Behavior and Diet */}
              <div style={{ backgroundColor: '#0d1117', border: '1px solid #21262d', borderRadius: '8px', padding: '14px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#4ade80', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Behavior and Diet
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', lineHeight: '1.45' }}>
                  {selectedSpecies.behavior_and_diet?.predation && (
                    <div><strong style={{ color: '#cbd5e1' }}>Predation:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.behavior_and_diet.predation}</span></div>
                  )}
                  {selectedSpecies.behavior_and_diet?.diet && (
                    <div><strong style={{ color: '#cbd5e1' }}>Diet:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.behavior_and_diet.diet}</span></div>
                  )}
                  {selectedSpecies.behavior_and_diet?.social_structure && (
                    <div><strong style={{ color: '#cbd5e1' }}>Social Structure:</strong> <span style={{ color: '#94a3b8' }}>{selectedSpecies.behavior_and_diet.social_structure}</span></div>
                  )}
                </div>
              </div>

              {/* 4. Distribution & Conservation */}
              {(selectedSpecies.distribution || selectedSpecies.conservation_status) && (
                <div style={{ backgroundColor: '#0d1117', border: '1px solid #21262d', borderRadius: '8px', padding: '14px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Distribution & Habitat
                  </h4>
                  {selectedSpecies.distribution && (
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.45' }}>
                      {selectedSpecies.distribution}
                    </p>
                  )}
                  {selectedSpecies.conservation_status && (
                    <div style={{ fontSize: '12px' }}>
                      <strong style={{ color: '#94a3b8' }}>Conservation Status:</strong>{' '}
                      <span style={{ color: '#38bdf8', fontWeight: '600' }}>{selectedSpecies.conservation_status}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleOpenEdit(selectedSpecies);
                }}
                style={{
                  padding: '7px 14px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'var(--card-bg, #21262d)',
                  color: 'var(--text-main, #c9d1d9)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                ✏️ Edit Record
              </button>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  padding: '7px 16px',
                  fontSize: '12px',
                  fontWeight: '700',
                  background: 'var(--primary-gradient, var(--primary-color, #38bdf8))',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ========================================================================= */}
      {/* 2. ADD / EDIT SPECIES MODAL (With AI & Web Quick Lookup)                   */}
      {/* ========================================================================= */}
      {showAddEditModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg, #161b22)',
            border: '1px solid var(--border-color, #30363d)',
            borderRadius: '12px',
            width: '760px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 16px 40px rgba(0,0,0,0.7)'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>
                  {editingSpecies ? `Edit Species: ${editingSpecies.common_name}` : 'Add New Species Profile'}
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                  Input species biological characteristics, or use AI lookup to auto-populate from internet databases.
                </span>
              </div>
              <button
                onClick={() => setShowAddEditModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mute, #8b949e)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                ✕
              </button>
            </div>

            {/* AI Lookup Banner */}
            <div style={{
              background: 'var(--panel-bg, #1b222d)',
              borderBottom: '1px solid var(--border-color, #30363d)',
              padding: '14px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#38bdf8' }}>
                <span>⚡</span> AI / Web Species Auto-Fill:
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Enter species name (e.g. 'Tiger fish', 'Micropterus salmoides', 'Elk')..."
                  value={aiLookupQuery}
                  onChange={(e) => setAiLookupQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAiLookup(); } }}
                  style={{
                    flex: '1',
                    backgroundColor: '#0d1117',
                    color: '#ffffff',
                    border: '1px solid #38bdf8',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  disabled={aiLookupLoading}
                  onClick={handleAiLookup}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: '700',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: aiLookupLoading ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {aiLookupLoading ? '🔍 Looking up...' : '⚡ Auto-Fill with AI'}
                </button>
              </div>
              {aiLookupSource && (
                <span style={{ fontSize: '11px', color: '#4ade80' }}>
                  ✓ Populated using {aiLookupSource === 'gemini_2_5_flash' ? 'Gemini 2.5 Flash' : 'Wikipedia REST API'}. Review and adjust any values below.
                </span>
              )}
            </div>

            {/* Form Fields Body */}
            <form onSubmit={handleSaveSpecies} style={{ display: 'flex', flexDirection: 'column', flex: '1', overflow: 'hidden' }}>
              <div style={{ padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {/* General Info */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#cbd5e1', textTransform: 'uppercase' }}>
                    1. Basic Identification
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Common Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.common_name}
                        onChange={(e) => setFormData({ ...formData, common_name: e.target.value })}
                        placeholder="e.g. Tiger fish"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Scientific Name</label>
                      <input
                        type="text"
                        value={formData.scientific_name}
                        onChange={(e) => setFormData({ ...formData, scientific_name: e.target.value })}
                        placeholder="e.g. Hydrocynus vittatus"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Category *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', boxSizing: 'border-box' }}
                      >
                        {modalCategories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Subcategory / Type</label>
                      <input
                        type="text"
                        value={formData.subcategory}
                        onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                        placeholder="e.g. Freshwater Game Fish"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Scientific Classification */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase' }}>
                    2. Scientific Classification
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Kingdom</label>
                      <input
                        type="text"
                        value={formData.kingdom}
                        onChange={(e) => setFormData({ ...formData, kingdom: e.target.value })}
                        placeholder="Animalia"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Phylum</label>
                      <input
                        type="text"
                        value={formData.phylum}
                        onChange={(e) => setFormData({ ...formData, phylum: e.target.value })}
                        placeholder="Chordata"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Order</label>
                      <input
                        type="text"
                        value={formData.order}
                        onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                        placeholder="e.g. Characiformes"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Family</label>
                      <input
                        type="text"
                        value={formData.family}
                        onChange={(e) => setFormData({ ...formData, family: e.target.value })}
                        placeholder="e.g. Alestidae (African tetras)"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Genus</label>
                      <input
                        type="text"
                        value={formData.genus}
                        onChange={(e) => setFormData({ ...formData, genus: e.target.value })}
                        placeholder="e.g. Hydrocynus ('water dog')"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Physical Characteristics */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#eab308', textTransform: 'uppercase' }}>
                    3. Physical Characteristics
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Teeth / Dentition</label>
                      <input
                        type="text"
                        value={formData.teeth}
                        onChange={(e) => setFormData({ ...formData, teeth: e.target.value })}
                        placeholder="e.g. Equipped with 32 interlocking, razor-sharp teeth..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Body / Morphology</label>
                      <input
                        type="text"
                        value={formData.body}
                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                        placeholder="e.g. Streamlined, heavily muscled, silver in color..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Jaws / Bite</label>
                      <input
                        type="text"
                        value={formData.jaws}
                        onChange={(e) => setFormData({ ...formData, jaws: e.target.value })}
                        placeholder="e.g. Powerful double-jointed jaw mechanism..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Size & Weight (Avg / Record)</label>
                      <input
                        type="text"
                        value={formData.size_weight}
                        onChange={(e) => setFormData({ ...formData, size_weight: e.target.value })}
                        placeholder="e.g. Up to 105 cm and 28 kg"
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Behavior and Diet */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#4ade80', textTransform: 'uppercase' }}>
                    4. Behavior and Diet
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Predation & Hunting Strategy</label>
                      <input
                        type="text"
                        value={formData.predation}
                        onChange={(e) => setFormData({ ...formData, predation: e.target.value })}
                        placeholder="e.g. Obligate carnivore hunting fast currents..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Diet</label>
                      <input
                        type="text"
                        value={formData.diet}
                        onChange={(e) => setFormData({ ...formData, diet: e.target.value })}
                        placeholder="e.g. Primarily piscivorous, known to catch birds in mid-air..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Social Structure</label>
                      <input
                        type="text"
                        value={formData.social_structure}
                        onChange={(e) => setFormData({ ...formData, social_structure: e.target.value })}
                        placeholder="e.g. Juveniles form schools, adults solitary..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Distribution & Image */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#c084fc', textTransform: 'uppercase' }}>
                    5. Distribution, Photo & Summary
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Geographic Distribution & Habitat</label>
                      <textarea
                        rows={2}
                        value={formData.distribution}
                        onChange={(e) => setFormData({ ...formData, distribution: e.target.value })}
                        placeholder="e.g. Congo River basin, Lake Tanganyika, Zambezi River..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box', resize: 'vertical' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Summary / Highlights</label>
                      <textarea
                        rows={2}
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        placeholder="Concise 2-3 sentence overview..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box', resize: 'vertical' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Representative Photo / Image URL</label>
                      <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://..."
                        style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    {isGlobalRole && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>Tenant Assignment (Optional)</label>
                        <select
                          value={formData.tenant_id}
                          onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                          style={{ width: '100%', backgroundColor: '#0d1117', color: '#ffffff', border: '1px solid #30363d', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                        >
                          <option value="">🌐 Global Library (Visible across matching tenants)</option>
                          {tenants.map((t) => (
                            <option key={t.id} value={t.id}>🏢 Tenant: {t.brand_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Error Notice */}
              {modalError && (
                <div style={{
                  margin: '0 20px 12px 20px',
                  padding: '10px 14px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '6px',
                  color: '#f87171',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '15px' }}>⚠️</span>
                  <span>{modalError}</span>
                </div>
              )}

              {/* Actions */}
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border-color, #30363d)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                background: 'var(--panel-bg, #0d1117)'
              }}>
                <button
                  type="button"
                  onClick={() => setShowAddEditModal(false)}
                  style={{
                    padding: '8px 14px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: 'transparent',
                    color: 'var(--text-mute, #94a3b8)',
                    border: '1px solid var(--border-color, #30363d)',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  style={{
                    padding: '8px 18px',
                    fontSize: '12px',
                    fontWeight: '700',
                    background: 'var(--primary-gradient, var(--primary-color, #38bdf8))',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isSaving ? 'wait' : 'pointer',
                    opacity: isSaving ? 0.7 : 1,
                    boxShadow: '0 2px 8px rgba(56, 189, 248, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {isSaving ? '⏳ Saving...' : (editingSpecies ? '✓ Update Species' : '✓ Save to Library')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ========================================================================= */}
      {/* 3. TENANT CATEGORY VISIBILITY MODAL (Global User Control)                 */}
      {/* ========================================================================= */}
      {showVisibilityModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 9999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg, #161b22)',
            border: '1px solid var(--border-color, #30363d)',
            borderRadius: '12px',
            width: '500px',
            maxWidth: '94vw',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 16px 40px rgba(0,0,0,0.7)'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>
                  ⚙️ Tenant Species Category Visibility
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-mute, #8b949e)' }}>
                  Control which animal and species types each tenant can view.
                </span>
              </div>
              <button
                onClick={() => setShowVisibilityModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mute, #8b949e)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Select Tenant */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #94a3b8)', marginBottom: '6px' }}>
                  Target Tenant:
                </label>
                <select
                  value={selectedVisibilityTenant}
                  onChange={(e) => handleSelectVisibilityTenant(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--input-bg, #0d1117)',
                    color: 'var(--text-main, #ffffff)',
                    border: '1px solid var(--border-color, #30363d)',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                >
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      🏢 {t.brand_name} ({t.domain_name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Checkboxes */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #94a3b8)', marginBottom: '8px' }}>
                  Allowed Species Categories:
                </label>
                <div style={{
                  background: 'var(--panel-bg, #0d1117)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {allCategories.map((cat) => {
                    const isChecked = visibilityAllowedCats.includes(cat);
                    const icon = CATEGORY_ICONS[cat] || '🐾';
                    return (
                      <label
                        key={cat}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '13px',
                          color: 'var(--text-main, #e2e8f0)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleVisibilityCategory(cat)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{icon}</span> {cat}
                      </label>
                    );
                  })}
                </div>
                <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-mute, #64748b)', marginTop: '6px' }}>
                  Note: If no categories are checked, the tenant will have unrestricted access to all categories.
                </span>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <button
                type="button"
                onClick={() => setShowVisibilityModal(false)}
                style={{
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: 'transparent',
                  color: 'var(--text-mute, #94a3b8)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTenantVisibility}
                style={{
                  padding: '8px 18px',
                  fontSize: '12px',
                  fontWeight: '700',
                  background: 'var(--primary-gradient, var(--primary-color, #38bdf8))',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                ✓ Save Filter
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
